// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Table,
  Badge,
  ActionIcon,
  Modal,
  TextInput,
  Select,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
  IconCalendar,
} from '@tabler/icons-react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useDisclosure } from '@mantine/hooks';
import { PageHeader } from '../../components/PageHeader';
import { useScheduleWeek, useCreateShift } from '../../hooks/useSchedules';
import { formatTime } from '../../utils/format';

interface Shift {
  id: string;
  employeeId: string;
  startTime: string;
  endTime: string;
  status: string;
  branchId: string;
  departmentId: string;
}

interface ScheduleWeekData {
  weekStart: string;
  weekEnd: string;
  shifts: Shift[];
}

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toISODate(date: Date): string {
  return date.toISOString().split('T')[0] ?? '';
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function SchedulePage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [newShift, setNewShift] = useState({
    employeeId: '',
    date: '',
    startTime: '09:00',
    endTime: '17:00',
  });

  const today = new Date();
  const currentWeekStart = getWeekStart(today);
  const weekStart = new Date(currentWeekStart);
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const { data: rawData, loading: isLoading } = useScheduleWeek(toISODate(weekStart));
  const scheduleData = rawData as unknown as ScheduleWeekData | undefined;
  const createShiftMutation = useCreateShift();

  const weekDays = useMemo(() => {
    const days: { date: string; dayName: string; dayNum: number }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push({
        date: toISODate(d),
        dayName: DAY_NAMES[i] ?? '',
        dayNum: d.getDate(),
      });
    }
    return days;
  }, [weekStart.toISOString()]);

  const shiftsByEmployee = useMemo(() => {
    const shifts = scheduleData?.shifts ?? [];
    const map = new Map<string, Map<string, Shift[]>>();
    for (const shift of shifts) {
      if (!map.has(shift.employeeId)) map.set(shift.employeeId, new Map());
      const dayMap = map.get(shift.employeeId)!;
      const shiftDate = shift.startTime.split('T')[0] ?? '';
      if (!dayMap.has(shiftDate)) dayMap.set(shiftDate, []);
      dayMap.get(shiftDate)!.push(shift);
    }
    return map;
  }, [scheduleData]);

  const employees = Array.from(shiftsByEmployee.keys()).sort();

  const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const handleCreateShift = () => {
    createShiftMutation.mutate({
      employeeId: newShift.employeeId,
      startTime: `${newShift.date}T${newShift.startTime}:00`,
      endTime: `${newShift.date}T${newShift.endTime}:00`,
    })
      .then(() => {
        closeAdd();
        setNewShift({ employeeId: '', date: '', startTime: '09:00', endTime: '17:00' });
      })
      .catch(() => {
        // Error is available via createShiftMutation.error
      });
  };

  const STATUS_COLORS: Record<string, string> = {
    scheduled: 'blue',
    confirmed: 'green',
    in_progress: 'teal',
    completed: 'gray',
    cancelled: 'red',
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Schedules"
        subtitle="View and manage employee work schedules"
        breadcrumbs={[
          { label: 'Schedule', to: '/admin/workforce/schedules' },
          { label: 'Weekly View' },
        ]}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={openAdd}>
            Add Shift
          </Button>
        }
      />

      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ActionIcon variant="subtle" onClick={() => setWeekOffset((o) => o - 1)}>
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text fw={600} size="lg">
              {weekLabel}
            </Text>
            <ActionIcon variant="subtle" onClick={() => setWeekOffset((o) => o + 1)}>
              <IconChevronRight size={18} />
            </ActionIcon>
            {weekOffset !== 0 && (
              <Button variant="subtle" size="xs" onClick={() => setWeekOffset(0)}>
                This Week
              </Button>
            )}
          </Group>
        </Group>

        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : employees.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No shifts scheduled for this week.
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Employee</Table.Th>
                {weekDays.map((day) => (
                  <Table.Th key={day.date} ta="center" style={{ minWidth: 100 }}>
                    <Text size="xs" c="dimmed">
                      {day.dayName}
                    </Text>
                    <Text size="sm">{day.dayNum}</Text>
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {employees.map((empId) => {
                const dayMap = shiftsByEmployee.get(empId)!;
                return (
                  <Table.Tr key={empId}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {empId}
                      </Text>
                    </Table.Td>
                    {weekDays.map((day) => {
                      const shifts = dayMap.get(day.date) ?? [];
                      return (
                        <Table.Td key={day.date} ta="center">
                          {shifts.length > 0 ? (
                            <Stack gap={2}>
                              {shifts.map((shift) => (
                                <Badge
                                  key={shift.id}
                                  size="sm"
                                  variant="light"
                                  color={STATUS_COLORS[shift.status] || 'gray'}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                                </Badge>
                              ))}
                            </Stack>
                          ) : (
                            <Text size="sm" c="dimmed">
                              -
                            </Text>
                          )}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Modal opened={addOpened} onClose={closeAdd} title="Add Shift" size="md">
        <Stack gap="sm">
          <TextInput
            label="Employee ID"
            placeholder="Enter employee ID"
            required
            value={newShift.employeeId}
            onChange={(e) => setNewShift((f) => ({ ...f, employeeId: e.currentTarget.value }))}
          />
          <Select
            label="Date"
            placeholder="Select day"
            required
            data={weekDays.map((d) => ({
              value: d.date,
              label: `${d.dayName} ${d.date}`,
            }))}
            value={newShift.date}
            onChange={(v) => setNewShift((f) => ({ ...f, date: v || '' }))}
          />
          <Group grow>
            <TextInput
              label="Start Time"
              type="time"
              required
              value={newShift.startTime}
              onChange={(e) => setNewShift((f) => ({ ...f, startTime: e.currentTarget.value }))}
            />
            <TextInput
              label="End Time"
              type="time"
              required
              value={newShift.endTime}
              onChange={(e) => setNewShift((f) => ({ ...f, endTime: e.currentTarget.value }))}
            />
          </Group>
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateShift}
              loading={createShiftMutation.isPending}
              disabled={!newShift.employeeId || !newShift.date}
            >
              Create Shift
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
